'use client'

import { useMemo } from 'react'
import { differenceInDays } from 'date-fns'
import type { Entry } from '@/document'
import { useDocumentView } from '@/document'

const STALE_THRESHOLD_DAYS = 14
const MAX_RESULTS = 3

interface StaleReviewsResult {
  /** Up to 3 stale tasks, sorted by staleness (most stale first) */
  staleTasks: Entry[]
  /** Total count of stale tasks (before cap) for "N more" indicator */
  count: number
}

const EMPTY_RESULT: StaleReviewsResult = {
  staleTasks: [],
  count: 0,
}

/**
 * Hook to get stale open tasks that haven't been updated in 14+ days.
 * Returns up to 3 tasks sorted by staleness (oldest first).
 */
export function useStaleReviews(): StaleReviewsResult {
  const doc = useDocumentView()

  return useMemo(() => {
    if (!doc) return EMPTY_RESULT

    const now = new Date()

    const staleTasks = Object.values(doc.entries)
      .filter((entry): entry is Entry => {
        if (entry.type !== 'task') return false
        if (entry.archivedAt) return false

        const status = (entry.metadata as { status?: string }).status
        if (status === 'done') return false

        const daysSinceUpdate = differenceInDays(now, new Date(entry.updatedAt))
        return daysSinceUpdate >= STALE_THRESHOLD_DAYS
      })
      .sort((a, b) => {
        // Most stale first (oldest updatedAt first)
        return a.updatedAt.localeCompare(b.updatedAt)
      })

    return {
      staleTasks: staleTasks.slice(0, MAX_RESULTS),
      count: staleTasks.length,
    }
  }, [doc])
}
